const BASE_URL = 'https://dify.acceptplay.com/v1';  // 使用 HTTPS URL
const DEFAULT_API_KEY = 'app-1oRlgS3kmL3cg7kZUtsouEle'; // Default API key

// Function to get the current API key from URL or use default
export function getApiKey(): string {
  // Get the full URL for debugging
  const fullUrl = window.location.href;
  console.log('Full URL in getApiKey:', fullUrl);
  
  // Get API key from URL search params if available
  const urlParams = new URLSearchParams(window.location.search);
  let apiKey = urlParams.get('api_key');
  
  // If we didn't find the API key in the search params, check if it's in the URL format mentioned by the user
  // Example: lay.com/?api_key=app-XjLd1WLtq
  if (!apiKey && fullUrl.includes('api_key=')) {
    const apiKeyMatch = fullUrl.match(/api_key=([^&]+)/);
    if (apiKeyMatch && apiKeyMatch[1]) {
      apiKey = apiKeyMatch[1];
      console.log('Found API key in URL using regex:', apiKey);
    }
  }
  
  console.log('Current URL:', window.location.href);
  console.log('URL Search Params:', window.location.search);
  console.log('API Key from URL:', apiKey);
  console.log('Using API Key:', apiKey || DEFAULT_API_KEY);
  
  // Return the API key from URL or default if not found
  return apiKey || DEFAULT_API_KEY;
}

// 使用原生 fetch API 代替 axios
async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const currentApiKey = getApiKey();
  
  console.log('Making API request to:', `${BASE_URL}${endpoint}`);
  console.log('Using API Key in request:', currentApiKey);
  
  const headers = {
    'Authorization': `Bearer ${currentApiKey}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }

  return response;
}

async function fetchJson<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetchWithAuth(endpoint, options);
  return response.json();
}

export interface ChatMessage {
  query: string;
  user: string;
  inputs: Record<string, any>;
  conversation_id?: string;
  response_mode?: 'streaming' | 'blocking';
  files?: Array<{
    type: string;
    transfer_method: 'remote_url' | 'local_file';
    url?: string;
    upload_file_id?: string;
  }>;
  auto_generate_name?: boolean;
}

export interface ConversationsResponse {
  data: Array<{
    id: string;
    name: string;
    created_at: number;
    updated_at: number;
  }>;
}

export interface MessagesResponse {
  data: Array<{
    id: string;
    conversation_id: string;
    query: string;
    answer: string;
    created_at: number;
  }>;
}

export interface AppParameters {
  introduction: string;
  suggested_questions: string[];
  suggested_questions_after_answer: {
    enabled: boolean;
  };
  speech_to_text: {
    enabled: boolean;
  };
  text_to_speech: {
    enabled: boolean;
    voice?: string;
    language?: string;
  };
  retriever_resource: {
    enabled: boolean;
  };
  annotation_reply: {
    enabled: boolean;
  };
  more_like_this: {
    enabled: boolean;
  };
  user_input_form: Array<{
    [key: string]: {
      label: string;
      variable: string;
      required: boolean;
      default: string;
    }
  }>;
  file_upload?: {
    image?: {
      enabled: boolean;
      number_limits: number;
      transfer_methods: string[];
    }
    enabled: boolean;
    allowed_file_types: string[];
    allowed_file_extensions: string[];
    allowed_file_upload_methods: string[];
    number_limits: number;
  };
  system_parameters?: {
    file_size_limit: number;
    image_file_size_limit: number;
    audio_file_size_limit: number;
    video_file_size_limit: number;
    workflow_file_upload_limit: number;
  };
}

export interface AppInfo {
  name: string;
  description: string;
  tags: string[];
}

export async function getConversations(userId: string, lastId?: string, limit = 20) {
  const params = new URLSearchParams({
    user: userId,
    limit: limit.toString(),
  });
  if (lastId) {
    params.append('last_id', lastId);
  }
  
  return fetchJson<ConversationsResponse>(`/conversations?${params.toString()}`);
}

export async function getMessages(conversationId: string, userId: string, firstId?: string, limit = 20) {
  const params = new URLSearchParams({
    conversation_id: conversationId,
    user: userId,
    limit: limit.toString(),
  });
  if (firstId) {
    params.append('first_id', firstId);
  }
  
  return fetchJson<MessagesResponse>(`/messages?${params.toString()}`);
}

export async function sendChatMessage(message: ChatMessage) {
  const response = await fetchWithAuth('/chat-messages', {
    method: 'POST',
    body: JSON.stringify(message),
  });

  // 对于流式响应，直接返回 response
  if (message.response_mode === 'streaming') {
    return response.body!;
  }

  // 对于非流式响应，返回解析后的 JSON
  return response.json();
}

export interface FileUploadResponse {
  url: string;
}

export async function uploadFile(file: File, user: string) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('user', user);
  
  return fetchJson<FileUploadResponse>('/files/upload', {
    method: 'POST',
    body: formData,
  });
}

export async function deleteConversation(conversationId: string, user: string) {
  return await fetchWithAuth(`/conversations/${conversationId}`, {
    method: 'DELETE',
    body: JSON.stringify({ user }),
  });
}

export async function audioToText(audioFile: File, user: string) {
  const formData = new FormData();
  formData.append('file', audioFile);
  formData.append('user', user);
  
  return fetchWithAuth('/audio-to-text', {
    method: 'POST',
    body: formData,
  });
}

export async function textToAudio(text: string, user: string, messageId?: string) {
  const formData = new FormData();
  formData.append('text', text);
  formData.append('user', user);
  if (messageId) {
    formData.append('message_id', messageId);
  }
  return await fetchWithAuth('/text-to-audio', {
    method: 'POST',
    body: formData,
  });
}

export async function getAppParameters(): Promise<AppParameters> {
  return fetchJson<AppParameters>('/parameters');
}

export async function getAppInfo(): Promise<AppInfo> {
  return fetchJson<AppInfo>('/info');
}
